import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  FormControl,
  FormLabel,
  FormGroup,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { fetchGet } from '../../../../../helpers/fetching';

const useStyles = makeStyles((theme) => ({
  titleSettingsCard: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    fontSize: '1.1rem',
  },

  module_title: {
    color: theme.palette.texts.secondary,
    fontSize: theme.typography.body2.fontSize,
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    padding: theme.spacing(0, 1),
    '&.Mui-focused': {
      color: theme.palette.texts.secondary,
    },
  },

  module_big_container: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    maxWidth: '100%',
  },

  module_section_container: {
    margin: theme.spacing(1),
    width: '50%',
    height: '100%',
  },

  form_control: {
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 2, 1.5),
    minHeight: '80px',
  },

  info_row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '2px',
    width: '100%',
    margin: 0,
  },

  info_label: {
    color: theme.palette.texts.secondary,
    fontSize: theme.fonts.responsiveMedium,
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },

  info_value: {
    color: theme.palette.texts.primary,
    textAlign: 'right',
    fontSize: theme.fonts.responsiveMediumSmall,
    textTransform: 'uppercase',
    wordBreak: 'break-all',
    textOverflow: 'ellipsis',
    maxWidth: '70%',
  },
}));

function InfoModule() {
  const classes = useStyles();
  const { t } = useTranslation();
  const globalSettings = useSelector((state) => state.global_settings);

  const [aiModelVersions, setAiModelVersions] = useState({});

  const qualityValues = Array.isArray(globalSettings?.quality_system?.quality_values)
    ? globalSettings.quality_system.quality_values
    : [];
  const formatSensitivity = (value) => (value ? t(`Settings.sensitivity_${value}`) : '-');

  const sensitivityRows = [
    {
      label: t('Settings.calc_sensitivity'),
      value: formatSensitivity(globalSettings?.sensitivity_calcifications),
    },
    {
      label: t('Settings.diagnostic_sensitivity'),
      value: formatSensitivity(globalSettings?.sensitivity_opacities),
    },
  ];

  const compressionRows = [
    {
      label: t('Settings.high_limit'),
      value: globalSettings?.compression_high ?? '-',
    },
    {
      label: t('Settings.low_limit'),
      value: globalSettings?.compression_low ?? '-',
    },
    {
      label: t('Settings.tolerance'),
      value: globalSettings?.compression_tolerance ?? '-',
    },
  ];

  const formatSectionLabel = (sectionKey) => t(`information.${sectionKey}`, sectionKey);
  const formatFieldLabel = (fieldKey) => t(`information.${fieldKey}`, fieldKey);

  const aiModelSectionPairs = [];
  const aiModelSections = Object.entries(aiModelVersions);
  for (let i = 0; i < aiModelSections.length; i += 2) {
    aiModelSectionPairs.push(aiModelSections.slice(i, i + 2));
  }

  const qualityRows = [
    {
      label: t('information.quality_system'),
      value: globalSettings?.quality_system?.quality_system || '-',
    },
    {
      label: t('information.quality_values'),
      value: qualityValues.length ? qualityValues.map((value) => t(`Quality.${value}`)).join(', ') : '-',
    },
  ];

  const fetchAiModelVersions = async () => {
    const response = await fetchGet('ai_model_versions', 'bbox');
    if (response.success) {
      setAiModelVersions(response.data);
    } else {
      console.error('Error fetching AI model versions:', response.msg);
    }
  };

  useEffect(() => {
    fetchAiModelVersions();
  }, []);

  return (
    <Grid container direction='column' spacing={1}>
      <Grid item>
        <Card elevation={1}>
          <CardHeader
            disableTypography={true}
            title={
              <Typography className={classes.titleSettingsCard} variant='h6'>
                {t('Menu.info')}
              </Typography>
            }
          />
          <CardContent style={{ paddingTop: '0px' }}>
            <div className={classes.module_big_container}>
              <div className={classes.module_section_container}>
                <FormControl component='fieldset' className={classes.form_control}>
                  <FormLabel component='legend' className={classes.module_title}>
                    {t('Settings.processing')}
                  </FormLabel>
                  <FormGroup>
                    <div className={classes.info_row}>
                      <Typography className={classes.info_label}>{t('Tomo')}:</Typography>
                      <Typography className={classes.info_value}>
                        {globalSettings?.premium_features?.processing_3d_tomo
                          ? t('Settings.yes')
                          : t('Settings.no')}
                      </Typography>
                    </div>
                    <div className={classes.info_row}>
                      <Typography className={classes.info_label}>{t('Settings.pull_priors_btn')}:</Typography>
                      <Typography className={classes.info_value}>
                        {globalSettings?.premium_features?.priors_fetching
                          ? t('Settings.yes')
                          : t('Settings.no')}
                      </Typography>
                    </div>
                  </FormGroup>
                </FormControl>
              </div>

              <div className={classes.module_section_container}>
                <FormControl component='fieldset' className={classes.form_control}>
                  <FormLabel component='legend' className={classes.module_title}>
                    b-Quality
                  </FormLabel>
                  <FormGroup>
                    {qualityRows.map((row) => (
                      <div key={row.label} className={classes.info_row}>
                        <Typography className={classes.info_label}>{row.label}:</Typography>
                        <Typography className={classes.info_value}>{row.value}</Typography>
                      </div>
                    ))}
                  </FormGroup>
                </FormControl>
              </div>
            </div>

            <div className={classes.module_big_container}>
              <div className={classes.module_section_container}>
                <FormControl component='fieldset' className={classes.form_control}>
                  <FormLabel component='legend' className={classes.module_title}>
                    b-Diagnostics
                  </FormLabel>
                  <FormGroup>
                    {sensitivityRows.map((row) => (
                      <div key={row.label} className={classes.info_row}>
                        <Typography className={classes.info_label}>{row.label}:</Typography>
                        <Typography className={classes.info_value}>{row.value}</Typography>
                      </div>
                    ))}
                  </FormGroup>
                </FormControl>
              </div>
              <div className={classes.module_section_container}>
                <FormControl component='fieldset' className={classes.form_control}>
                  <FormLabel component='legend' className={classes.module_title}>
                    {t('ResultView.compression')}
                  </FormLabel>
                  <FormGroup>
                    {compressionRows.map((row) => (
                      <div key={row.label} className={classes.info_row}>
                        <Typography className={classes.info_label}>{row.label}:</Typography>
                        <Typography className={classes.info_value}>{row.value}</Typography>
                      </div>
                    ))}
                  </FormGroup>
                </FormControl>
              </div>
            </div>

            {aiModelSectionPairs.map((pair, pairIndex) => (
              <div key={pairIndex} className={classes.module_big_container}>
                {pair.map(([sectionKey, models]) => {
                  const modelEntries = Object.entries(models);
                  const showModelName = modelEntries.length > 1 || modelEntries[0]?.[0] !== sectionKey;

                  return (
                    <div key={sectionKey} className={classes.module_section_container}>
                      <FormControl component='fieldset' className={classes.form_control}>
                        <FormLabel component='legend' className={classes.module_title}>
                          {formatSectionLabel(sectionKey)}
                        </FormLabel>
                        <FormGroup>
                          {modelEntries.map(([modelName, fields]) => (
                            <div key={modelName}>
                              {showModelName && (
                                <Typography
                                  className={classes.info_label}
                                  style={{ marginTop: 8, textDecoration: 'underline' }}>
                                  {modelName}
                                </Typography>
                              )}
                              {Object.entries(fields)
                                .filter(([fieldKey]) => fieldKey !== 'digest')
                                .map(([fieldKey, value]) => (
                                  <div key={`${modelName}-${fieldKey}`} className={classes.info_row}>
                                    <Typography className={classes.info_label}>
                                      {formatFieldLabel(fieldKey)}:
                                    </Typography>
                                    <Typography className={classes.info_value}>{String(value)}</Typography>
                                  </div>
                                ))}
                            </div>
                          ))}
                        </FormGroup>
                      </FormControl>
                    </div>
                  );
                })}
              </div>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default InfoModule;
